import { chemStyles } from "./chemStyles";
// EA's AcceleRATE height bands (cm). Female Lengthy floor moved 165 -> 172
// for FC 27 (DEV-3688). Heights default to the male thresholds when gender is
// missing, since there are more male players than female ones.
export const EXPLOSIVE_MAX_HEIGHT = { MALE: 183, FEMALE: 163 };
export const LENGTHY_MIN_HEIGHT = { MALE: 185, FEMALE: 172 };
const isExplosive = (agility, acceleration, strength, gender, height) => {
    const maxHeight = EXPLOSIVE_MAX_HEIGHT[gender];
    return (agility >= 65 &&
        acceleration >= 80 &&
        agility - strength >= 10 &&
        height <= maxHeight);
};
const isLengthy = (agility, acceleration, strength, gender, height) => {
    const minHeight = LENGTHY_MIN_HEIGHT[gender];
    return (strength >= 65 &&
        acceleration >= 40 &&
        strength - agility >= 4 &&
        height >= minHeight);
};
export function calculateAccelerate(agility, acceleration, strength, height, gender) {
    if (isExplosive(agility, acceleration, strength, gender, height)) {
        return "Explosive";
    }
    else if (isLengthy(agility, acceleration, strength, gender, height)) {
        return "Lengthy";
    }
    return "Controlled";
}
/** Which AcceleRATEs a player's height allows at all, whatever their stats. */
export const canBecomeAccelerates = (height, gender) => {
    const accelerates = ["Controlled"];
    if (height <= EXPLOSIVE_MAX_HEIGHT[gender])
        accelerates.push("Explosive");
    if (height >= LENGTHY_MIN_HEIGHT[gender])
        accelerates.push("Lengthy");
    return accelerates;
};
/**
 * The AcceleRATE a player lands on with a chemstyle applied at a chemistry
 * level: the style's agility/strength/acceleration bonuses on top of the
 * in-game stats, through `calculateAccelerate`. Undefined while any of the
 * three stats is missing.
 *
 * Port of web's `functions/chemStyleAccelerate.ts`. Web routes the lookup
 * through `calculatePlayerStatsBonus`, but only its `specificStatsBonus` is
 * read — which is just `chemstyleBonuses[styleId][chemistry]` — so the weighted
 * face-card math isn't needed here.
 */
export const getChemStyleAccelerate = ({ chemStyleId, chemistry, inGameAttributes, height, gender, chemstyleBonuses, }) => {
    // No chemistry means no stat bonuses.
    const bonuses = chemistry === 0
        ? undefined
        : chemstyleBonuses?.[`${chemStyleId}`]?.[`${chemistry}`];
    const agility = (inGameAttributes["agility"] ?? 0) + (bonuses?.["agility"] || 0);
    const strength = (inGameAttributes["strength"] ?? 0) + (bonuses?.["strength"] || 0);
    const acceleration = (inGameAttributes["acceleration"] ?? 0) + (bonuses?.["acceleration"] || 0);
    if (!agility || !strength || !acceleration)
        return undefined;
    return calculateAccelerate(Math.min(agility, 99), Math.min(acceleration, 99), Math.min(strength, 99), height, gender);
};
/** Chemstyle ids from here up are the goalkeeper-only styles. */
export const GK_CHEMSTYLE_ID_START = 269;
/**
 * Every applicable chemstyle (outfield < 269 ≤ GK), grouped by the AcceleRATE
 * it gives the player. Grouped at full chemistry when `chemistry` is 0 — zero
 * chem applies no bonuses, which would collapse every style into one group.
 * Empty groups are dropped; order is Lengthy → Controlled → Explosive (the
 * DetailedPlayer section's order).
 */
export const groupChemStylesByAccelerate = ({ isGk, chemistry, ...inputs }) => {
    const groups = {
        Lengthy: [],
        Controlled: [],
        Explosive: [],
    };
    for (const [idKey, name] of Object.entries(chemStyles)) {
        const id = +idKey;
        if (isGk !== id >= GK_CHEMSTYLE_ID_START)
            continue;
        const accelerate = getChemStyleAccelerate({
            ...inputs,
            chemistry: chemistry === 0 ? 3 : chemistry,
            chemStyleId: id,
        });
        if (accelerate && groups[accelerate])
            groups[accelerate].push({ id, name });
    }
    return Object.entries(groups)
        .filter(([, styles]) => styles.length > 0)
        .map(([accelerate, styles]) => ({ accelerate, chemStyles: styles }));
};
/**
 * The applicable chemstyles as one ungrouped list — the fallback for when the
 * player's in-game stats or the chemstyle bonuses haven't arrived yet (compare
 * cards before the detailed fetch enriches them).
 */
export const allChemStyles = (isGk) => Object.entries(chemStyles)
    .map(([id, name]) => ({ id: +id, name }))
    .filter(({ id }) => isGk === id >= GK_CHEMSTYLE_ID_START);
//# sourceMappingURL=chemStyleAccelerate.js.map